"use client";
import { createContext, useContext, useState, useEffect } from 'react'
import { usePathname, useSelectedLayoutSegments } from 'next/navigation'
import authService, { SUPPLIER_ME_TIMEOUT_MS } from '../services/authService'
import { useFirebaseSocialAuth } from '../hooks/useFirebaseSocialAuth'
import supplierOnboardingService from '../services/supplierOnboardingService'
import googleAuthService from '../services/googleAuthService'
import { isSupplierPortalRoute } from '../layouts/supplierPortalRoutes'
import { SUPPLIER_SESSION_EXPIRED_EVENT } from '../utils/supplierSessionEvents'

const AuthContext = createContext()
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
// Export AuthContext for safe access when needed
export { AuthContext }
export const AuthProvider = ({ children }) => {
  const pathname = usePathname()
  const segments = useSelectedLayoutSegments()
  const portalActive = isSupplierPortalRoute(pathname, segments[0])
  
  const isPublicSupplierRoute = 
    pathname?.startsWith('/supplier/auth/invitation') ||
    pathname?.startsWith('/supplier/forgot-password') ||
    pathname?.startsWith('/supplier/reset-password') ||
    pathname?.startsWith('/supplier/auth/google/callback');

  const sessionProtectedRoute =
    portalActive && !pathname?.startsWith('/onboarding') && !isPublicSupplierRoute
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(sessionProtectedRoute)
  const firebaseSocialAuth = useFirebaseSocialAuth()

  const setSupplierOnboardingStatus = (status, { rejectionReason = null } = {}) => {
    try {
      localStorage.setItem('supplier_onboarding_status', status)
      sessionStorage.setItem('supplier_onboarding_status', status)
      if (rejectionReason) {
        localStorage.setItem('supplier_rejection_reason', rejectionReason)
        sessionStorage.setItem('supplier_rejection_reason', rejectionReason)
      } else {
        localStorage.removeItem('supplier_rejection_reason')
        sessionStorage.removeItem('supplier_rejection_reason')
      }
    } catch (_) {}
  }

  const syncOnboardingStatusFromBackend = async (userData) => {
    try {
      const email = userData?.email || ''
      if (!email) return

      // Verified users are always verified
      if (userData?.is_verified) {
        setSupplierOnboardingStatus('verified')
        return
      }

      // Check userData status first (since GET /me now returns status and rejection_reason)
      const userStatus = (userData?.status || '').toString().toLowerCase().trim()
      if (userStatus === 'rejected') {
        setSupplierOnboardingStatus('rejected', {
          rejectionReason: userData?.rejection_reason || 'Your application was rejected.',
        })
        return
      }
      if (userStatus === 'approved') {
        setSupplierOnboardingStatus('verified')
        return
      }
      if (userStatus === 'pending' || userStatus === 'in_review') {
        setSupplierOnboardingStatus('pending_admin_review')
        return
      }

      const previousStatus =
        localStorage.getItem('supplier_onboarding_status') ||
        sessionStorage.getItem('supplier_onboarding_status') ||
        null

      const progress = await supplierOnboardingService.getOnboardingByEmail(email)
      const backendStatus = (progress?.status || '').toString().toLowerCase().trim()

      if (backendStatus === 'approved') {
        // Backend approved but userData.is_verified might be stale in UI, keep this as verified gate
        setSupplierOnboardingStatus('verified')
        return
      }

      if (backendStatus === 'pending' || backendStatus === 'in_review') {
        setSupplierOnboardingStatus('pending_admin_review')
        return
      }

      // If onboarding was deleted/removed after being pending, treat as rejected in UI
      if (!progress && previousStatus && previousStatus.startsWith('pending')) {
        setSupplierOnboardingStatus('rejected', {
          rejectionReason: 'Your supplier application was removed by admin. Please contact support.',
        })
      }
    } catch (error) {
      // If backend unavailable, keep current status
      }
  }

  const hydrateBasicProfileFromUser = (userData) => {
    if (!userData) return
    try {
      const fallbackName =
        (userData.full_name ||
          userData.business_name ||
          userData.email?.split('@')[0] ||
          'TripsAdora Supplier')
          .toString()
          .trim() || 'TripsAdora Supplier'

      const fallbackCity = (userData.city || userData.location || '').trim()
      const fallbackCountry = (userData.country || '').trim()
      const fallbackEmail = (userData.email || '').trim()
      const businessLegal = (userData.business_name || '').trim()
      const streetFromProfile = (userData.address || '').trim()
      const phoneRaw = (userData.phone != null ? String(userData.phone) : '').trim()
      const supplierCode =
        userData.supplier_code ||
        localStorage.getItem('supplier_id') ||
        sessionStorage.getItem('supplier_id') ||
        ''
      const photo =
        userData.avatar_url ||
        userData.profile_photo ||
        userData.photo_url ||
        null

      const existingRaw =
        localStorage.getItem('account_settings') ||
        sessionStorage.getItem('account_settings')

      let parsed =
        existingRaw && existingRaw !== 'undefined'
          ? JSON.parse(existingRaw)
          : null

      if (!parsed || typeof parsed !== 'object') {
        parsed = {}
      } else {
        parsed = { ...parsed }
      }

      parsed.editableInfo = { ...(parsed.editableInfo || {}) }
      parsed.profileData = { ...(parsed.profileData || {}) }

      let changed = !existingRaw

      const ensureValue = (target, key, value) => {
        if (!target[key] && value) {
          target[key] = value
          changed = true
        }
      }

      ensureValue(parsed.editableInfo, 'publicName', fallbackName)
      ensureValue(parsed.editableInfo, 'legalName', businessLegal || fallbackName)
      ensureValue(parsed.editableInfo, 'email', fallbackEmail)
      ensureValue(parsed.editableInfo, 'streetAddress', streetFromProfile)
      ensureValue(parsed.editableInfo, 'city', fallbackCity)
      ensureValue(parsed.editableInfo, 'country', fallbackCountry)

      ensureValue(parsed.profileData, 'publicName', fallbackName)
      ensureValue(parsed.profileData, 'email', fallbackEmail)
      ensureValue(parsed.profileData, 'streetAddress', streetFromProfile)
      ensureValue(parsed.profileData, 'supplierCode', supplierCode)
      ensureValue(parsed.profileData, 'contactValue', supplierCode)
      if (!parsed.profileData.contactLabel) {
        parsed.profileData.contactLabel = 'Supplier ID'
        changed = true
      }
      ensureValue(parsed.profileData, 'city', fallbackCity)
      ensureValue(parsed.profileData, 'country', fallbackCountry)

      const hasContactValue =
        Array.isArray(parsed.contactNumbers) &&
        parsed.contactNumbers.some((c) => c && String(c.value || '').trim() !== '')
      if (phoneRaw && !hasContactValue) {
        parsed.contactNumbers = [{ id: 1, type: 'WhatsApp number', value: phoneRaw }]
        changed = true
      }

      if (!parsed.globalUserPhoto && photo) {
        parsed.globalUserPhoto = photo
        changed = true
      }

      if (changed) {
        const serialized = JSON.stringify(parsed)
        localStorage.setItem('account_settings', serialized)
        sessionStorage.setItem('account_settings', serialized)
        window.dispatchEvent(new Event('account_settings_updated'))
        window.dispatchEvent(new Event('accountSettingsUpdated'))
      }
    } catch (error) {
      }
  }



  const initializeAuth = async (signal) => {
    // Phase 3: session restore ONLY via GET /supplier/auth/me (httpOnly cookies).
    // Never gate on localStorage JWTs or decode tokens.
    const maxTimeout = setTimeout(() => {
      if (!signal?.aborted) setLoading(false)
    }, SUPPLIER_ME_TIMEOUT_MS)

    try {
      try {
        localStorage.removeItem('supplier_access_token')
        localStorage.removeItem('supplier_refresh_token')
      } catch (_) {}

      try {
        console.log("[supplier-auth-debug] event: me-request-start, source: AuthContext");
        const userData = await authService.getCurrentUser({ signal })

        if (signal?.aborted) return

        console.log("[supplier-auth-debug] event: me-success, status:", userData?.status, "is_active:", userData?.is_active, "verification_status:", userData?.verification_status, "blocked_reason:", userData?.blocked_reason);

        if (userData) {
          if (userData.blocked || (!userData.is_active && userData.blocked_reason)) {
            const blockedReason = userData.blocked_reason || userData.reason || 'Your account has been blocked.'
            localStorage.setItem('supplier_blocked_reason', blockedReason)
            localStorage.setItem('supplier_onboarding_status', 'blocked')
            sessionStorage.setItem('supplier_onboarding_status', 'blocked')
            authService.setSessionActive(false)
            console.log("[supplier-auth-debug] event: logout, reason: user is blocked or inactive with blocked_reason, source: AuthContext");
            window.location.href = '/'
            return
          }
          persistSupplierId(userData?.supplier_code)
          try {
            const status = (userData?.status || '').toLowerCase()
            const statusFlag = status === 'rejected' ? 'rejected' : (userData?.is_verified ? 'verified' : 'pending_admin_review')
            localStorage.setItem('supplier_onboarding_status', statusFlag)
            sessionStorage.setItem('supplier_onboarding_status', statusFlag)
          } catch (err) {
            }
          await syncOnboardingStatusFromBackend(userData)
          if (signal?.aborted) return
          hydrateBasicProfileFromUser(userData)
          authService.setSessionActive(true)
          setUser(userData)
        } else {
          console.log("[supplier-auth-debug] event: logout, reason: userData is null after getCurrentUser, source: AuthContext");
          authService.clearLocalSession()
          setUser(null)
          if (sessionProtectedRoute) {
            console.log("[supplier-auth-debug] event: redirect, from: AuthContext, to: /onboarding/login?session=expired, reason: userData is null on protected route");
            window.location.replace('/onboarding/login?session=expired')
            return
          }
        }
      } catch (error) {
        if (error?.name === 'AbortError' || signal?.aborted) return
        console.error('Failed to get current user (supplier):', error)
        const status = error?.response?.status
        console.log(`[supplier-auth-debug] event: me-request-error, status: ${status}, source: AuthContext`);
        if (status === 401) {
          try {
            localStorage.removeItem('supplier_access_token')
            localStorage.removeItem('supplier_refresh_token')
          } catch (_) {}
          authService.setSessionActive(false)
        }
        setUser(null)
      }
    } catch (error) {
      if (error?.name === 'AbortError' || signal?.aborted) return
      console.error('Auth initialization error:', error)
      setUser(null)
      authService.setSessionActive(false)
    } finally {
      clearTimeout(maxTimeout)
      if (!signal?.aborted) setLoading(false)
    }
  }

  useEffect(() => {
    if (!sessionProtectedRoute) {
      setLoading(false)
      return undefined
    }
    const controller = new AbortController()
    initializeAuth(controller.signal)
    return () => controller.abort()
  }, [sessionProtectedRoute])

  useEffect(() => {
    const handleSessionExpired = (e) => {
      const reason = e?.detail?.reason || "unknown";
      console.log(`[supplier-auth-debug] event: logout, reason: session expired (${reason}), source: SUPPLIER_SESSION_EXPIRED_EVENT`);
      authService.clearLocalSession()
      setUser(null)
      setLoading(false)

      if (sessionProtectedRoute) {
        console.log(`[supplier-auth-debug] event: redirect, from: AuthContext event handler, to: /onboarding/login?session=expired, reason: session expired (${reason})`);
        window.location.replace('/onboarding/login?session=expired')
      }
    }

    window.addEventListener(
      SUPPLIER_SESSION_EXPIRED_EVENT,
      handleSessionExpired,
    )
    return () => {
      window.removeEventListener(
        SUPPLIER_SESSION_EXPIRED_EVENT,
        handleSessionExpired,
      )
    }
  }, [sessionProtectedRoute])
  
  const persistSupplierId = (incomingSupplierId) => {
    try {
      let supplierId = (incomingSupplierId || '').trim()
      if (!supplierId) {
        supplierId = localStorage.getItem('supplier_id') || sessionStorage.getItem('supplier_id') || ''
      }
      if (!supplierId) {
        const saved = localStorage.getItem('account_settings') || sessionStorage.getItem('account_settings')
        if (saved) {
          try {
            const data = JSON.parse(saved)
            supplierId =
              data?.profileData?.supplierCode ||
              data?.profileData?.contactValue ||
              ''
          } catch (_) {
            supplierId = ''
          }
        }
      }
      if (!supplierId) {
        return
      }

      localStorage.setItem('supplier_id', supplierId)
      sessionStorage.setItem('supplier_id', supplierId)

      const saved = localStorage.getItem('account_settings') || sessionStorage.getItem('account_settings')
      if (saved) {
        try {
          const data = JSON.parse(saved)
          const updated = {
            ...data,
            profileData: {
              ...(data?.profileData || {}),
              supplierCode: supplierId,
              contactValue: supplierId,
              contactLabel: data?.profileData?.contactLabel || 'Supplier ID'
            }
          }
          const serialized = JSON.stringify(updated)
          localStorage.setItem('account_settings', serialized)
          sessionStorage.setItem('account_settings', serialized)
          window.dispatchEvent(new Event('account_settings_updated'))
        } catch (_) {}
      }
    } catch (_) {}
  }

  const login = async (credentials) => {
    try {
      setLoading(true)
      const result = await authService.login(credentials)
      
      if (result.require_workspace_selection) {
        return result
      }
      
      // Check if user is blocked (from error response)
      if (result.blocked) {
        localStorage.setItem('supplier_blocked_reason', result.reason || 'Your account has been blocked.')
        localStorage.setItem('supplier_onboarding_status', 'blocked')
        sessionStorage.setItem('supplier_onboarding_status', 'blocked')
        // Redirect to rejection screen immediately
        window.location.href = '/'
        return { success: false, blocked: true, reason: result.reason }
      }
      
      if (result.success) {
        // Check if user is blocked (from user object - double check)
        if (!result.user?.is_active && result.user?.blocked_reason) {
          localStorage.setItem('supplier_blocked_reason', result.user.blocked_reason)
          localStorage.setItem('supplier_onboarding_status', 'blocked')
          sessionStorage.setItem('supplier_onboarding_status', 'blocked')
          // Redirect to rejection screen
          window.location.href = '/'
          return { success: false, blocked: true, reason: result.user.blocked_reason }
        }
        persistSupplierId(result.user?.supplier_code)
        // If not verified yet, keep user logged-in for pending dashboard flow
        // (Do NOT redirect away from supplier portal)
        try {
          const statusFlag = result.user?.is_verified ? 'verified' : 'pending_admin_review'
          localStorage.setItem('supplier_onboarding_status', statusFlag)
          sessionStorage.setItem('supplier_onboarding_status', statusFlag)
        } catch (err) {
          }
        await syncOnboardingStatusFromBackend(result.user)
        hydrateBasicProfileFromUser(result.user)
        authService.setSessionActive(true)
        setUser(result.user)
        return { success: true, user: result.user }
      }
      
      throw new Error('Login failed')
    } catch (error) {
      console.error('Login error:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // OAuth redirect flow (no Google Cloud Console configuration needed for /supplier/auth/google/callback)
  const googleLogin = async () => {
    const clientId = (googleAuthService.clientId || "").trim();
    if (!clientId) {
      throw new Error(
        "Google sign-in is not configured. Set NEXT_PUBLIC_GOOGLE_CLIENT_ID in .env.",
      );
    }

    // Use OAuth redirect flow instead of Firebase popup
    // This uses /supplier/auth/google/callback which is already configured in backend
    const redirectUri = window.location.origin + '/supplier/auth/google/callback';
    
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(clientId)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `scope=openid%20email%20profile&` +
      `access_type=offline`;
    
    // Redirect to Google OAuth
    window.location.href = googleAuthUrl;
  }

  const loginWithGoogleFirebase = async () => {
    setLoading(true)
    try {
      const response = await firebaseSocialAuth.loginWithGoogle()
      if (response?.success) {
        persistSupplierId(response.user?.supplier_code)
        try {
          const statusFlag = response.user?.is_verified ? 'verified' : 'pending_admin_review'
          localStorage.setItem('supplier_onboarding_status', statusFlag)
          sessionStorage.setItem('supplier_onboarding_status', statusFlag)
        } catch (err) {
          }
        await syncOnboardingStatusFromBackend(response.user)
        hydrateBasicProfileFromUser(response.user)
        setUser(response.user)
        return { success: true, user: response.user }
      }
      throw new Error('Firebase Google login failed')
    } finally {
      setLoading(false)
    }
  }

  const loginWithFacebookFirebase = async () => {
    setLoading(true)
    try {
      const response = await firebaseSocialAuth.loginWithFacebook()
      if (response?.success) {
        persistSupplierId(response.user?.supplier_code)
        try {
          const statusFlag = response.user?.is_verified ? 'verified' : 'pending_admin_review'
          localStorage.setItem('supplier_onboarding_status', statusFlag)
          sessionStorage.setItem('supplier_onboarding_status', statusFlag)
        } catch (err) {
          }
        hydrateBasicProfileFromUser(response.user)
        setUser(response.user)
        return { success: true, user: response.user }
      }
      throw new Error('Firebase Facebook login failed')
    } finally {
      setLoading(false)
    }
  }

  const loginWithAppleFirebase = async () => {
    setLoading(true)
    try {
      const response = await firebaseSocialAuth.loginWithApple()
      if (response?.success) {
        persistSupplierId(response.user?.supplier_code)
        try {
          const statusFlag = response.user?.is_verified ? 'verified' : 'pending_admin_review'
          localStorage.setItem('supplier_onboarding_status', statusFlag)
          sessionStorage.setItem('supplier_onboarding_status', statusFlag)
        } catch (err) {
          }
        hydrateBasicProfileFromUser(response.user)
        setUser(response.user)
        return { success: true, user: response.user }
      }
      throw new Error('Firebase Apple login failed')
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData) => {
    try {
      setLoading(true)
      const result = await authService.register(userData)
      
      if (result.success) {
        persistSupplierId(result.user?.supplier_code)
        // New suppliers are not verified, redirect to registration page
        const customerAppUrl = process.env.NEXT_PUBLIC_TRIPSADORA_FRONTEND_URL || 'http://localhost:5173'
        try {
          localStorage.setItem('supplier_onboarding_status', 'pending_admin_review')
          sessionStorage.setItem('supplier_onboarding_status', 'pending_admin_review')
        } catch (err) {
          }
        hydrateBasicProfileFromUser(result.user)
        setUser(result.user)
        // Redirect to customer platform registration page with email parameter
        const email = result.user?.email || ''
        const redirectUrl = email 
          ? `${customerAppUrl}/`
          : `${customerAppUrl}/`
        window.location.href = redirectUrl
        return { success: true, requiresVerification: true }
      }
      
      throw new Error('Registration failed')
    } catch (error) {
      console.error('Registration error:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }


  const logout = async () => {
    try {
      setLoading(true)
      try {
        await authService.logout()
      } catch (_) {
        /* still clear local state below via authService.finally */
      }
      await firebaseSocialAuth.logoutFirebase().catch(() => {})
      setUser(null)
      // authService.logout already clears supplier caches; ensure leftovers gone
      try {
        localStorage.removeItem('supplier_access_token')
        localStorage.removeItem('supplier_refresh_token')
        localStorage.removeItem('supplier_id')
        sessionStorage.removeItem('supplier_id')
        localStorage.removeItem('account_settings')
        sessionStorage.removeItem('account_settings')
        localStorage.removeItem('supplier_profile')
        sessionStorage.removeItem('supplier_profile')
        localStorage.removeItem('supplier_onboarding_status')
        localStorage.removeItem('supplier_blocked_reason')
        localStorage.removeItem('supplier_rejection_reason')
        localStorage.removeItem('supplier_dashboard_cache')
        localStorage.removeItem('supplier_dashboard_cache_ts')
        localStorage.removeItem('notifications')
        localStorage.removeItem('persist:tripsadora_chat')
        const keys = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && key.startsWith('supplier_onboarding_progress_')) keys.push(key)
        }
        keys.forEach((k) => localStorage.removeItem(k))
      } catch (_) {}
    } catch (error) {
      console.error('[AuthContext] Logout error:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const updateUser = (userData) => {
    if (!userData) return
    setUser(prev => ({ ...prev, ...userData }))
    hydrateBasicProfileFromUser(userData)
  }

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    googleLogin,
    loginWithGoogleFirebase,
    loginWithFacebookFirebase,
    loginWithAppleFirebase,
    isAuthenticated: !!user,
    isSupplier: user?.role === 'supplier' || !!user?.supplier_code
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
